import { UseQueryOptions, useQuery } from 'react-query';
import { axiosClient } from './axiosClient';

const defaultQueryFn = async (url: string) => {
  const { data } = await axiosClient.get(url);
  return data;
};

const defaultOptions = {
  refetchOnWindowFocus: false,
  retry: 0,
};

type UseGetQueryOptions<TQueryFnData, TError, TData> = {
  // eslint-disable-next-line no-unused-vars
  transformFn?: (data: any) => any;
} & UseQueryOptions<TQueryFnData, TError, TData>;

/**
 * Convenience wrapper around useQuery to handle common GET requests
 * @param url Path to retrieve data
 * @param options Optional query configuration which extends useQuery's options with the following
 * @param options.transformFn Function to modify the response data
 */
export function useGetQuery<TQueryFnData = unknown, TError = unknown, TData = TQueryFnData>(
  url: string,
  options: UseGetQueryOptions<TQueryFnData, TError, TData> = {},
) {
  const { transformFn, queryKey, ...useQueryOptions } = options;
  // react-query passes the key to the queryFn but we need the url
  let queryFn = () => defaultQueryFn(url);

  if (transformFn) {
    // react-query passes the key to the queryFn but we need url
    queryFn = () => defaultQueryFn(url).then(transformFn);
  }

  const queryOpts = {
    ...defaultOptions,
    ...useQueryOptions,
    queryFn,
    queryKey: queryKey || url,
  };

  return useQuery<TQueryFnData, TError, TData>(queryOpts);
}
